import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { API } from "../App";
import { ArrowLeft, Send, Loader2 } from "lucide-react";
import FuturaIcon from "../components/FuturaIcon";
import axios from "axios";

const SimulationChat = () => {
  const navigate = useNavigate();
  const { decisionId } = useParams();
  const [decision, setDecision] = useState(null);
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [progress, setProgress] = useState({ current: 0, total: 8 });
  const [answer, setAnswer] = useState("");
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [simulating, setSimulating] = useState(false);
  const msgIdRef = useRef(0);

  const addMessage = useCallback((msg) => {
    msgIdRef.current += 1;
    setMessages(prev => [...prev, { ...msg, _id: msgIdRef.current }]);
  }, []);

  const fetchNextQuestion = useCallback(async () => {
    try {
      const response = await axios.post(
        `${API}/decisions/${decisionId}/questions`,
        {},
        { withCredentials: true }
      );

      if (response.data.complete) {
        return true; // signals simulation needed
      } else {
        setCurrentQuestion(response.data.question);
        setProgress(response.data.progress);
        addMessage({
          type: "ai",
          text: response.data.question.question_text,
          questionType: response.data.question.question_type,
          options: response.data.question.options
        });
        return false;
      }
    } catch (error) {
      return false;
    }
  }, [decisionId, addMessage]);

  const runSimulation = useCallback(async () => {
    setSimulating(true);
    addMessage({ type: "ai", text: "Analizando tu información... Esto puede tomar unos segundos." });

    try {
      await axios.post(
        `${API}/decisions/${decisionId}/simulate`,
        {},
        { withCredentials: true }
      );
      navigate(`/result/${decisionId}`);
    } catch (error) {
      addMessage({ type: "ai", text: "Hubo un error al procesar la simulación. Por favor intenta de nuevo." });
      setSimulating(false);
    }
  }, [decisionId, navigate, addMessage]);

  useEffect(() => {
    const fetchDecision = async () => {
      try {
        const response = await axios.get(`${API}/decisions/${decisionId}`, {
          withCredentials: true
        });
        setDecision(response.data);
        addMessage({
          type: "ai",
          text: `Voy a analizar tu decisión: "${response.data.title}". Te haré algunas preguntas para entender mejor tu situación.`
        });
        const needsSim = await fetchNextQuestion();
        if (needsSim) await runSimulation();
      } catch (error) {
        navigate("/dashboard");
      } finally {
        setLoading(false);
      }
    };
    fetchDecision();
  }, [decisionId, navigate, addMessage, fetchNextQuestion, runSimulation]);

  const handleSubmitAnswer = async (selectedAnswer) => {
    const answerText = selectedAnswer || answer;
    if (!answerText.trim() || !currentQuestion) return;

    setSubmitting(true);
    setAnswer("");
    addMessage({ type: "user", text: answerText });

    try {
      const response = await axios.post(
        `${API}/decisions/${decisionId}/answer`,
        {
          question_id: currentQuestion.question_id,
          answer: answerText
        },
        { withCredentials: true }
      );

      if (response.data.complete) {
        await runSimulation();
      } else {
        const needsSim = await fetchNextQuestion();
        if (needsSim) await runSimulation();
      }
    } catch (error) {
      addMessage({ type: "ai", text: "Hubo un error. Por favor intenta de nuevo." });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F5F5F7] flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-[#002FA7] border-t-transparent rounded-full animate-spin"></div>
          <p className="text-[#6B7280]">Cargando...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F5F7] flex flex-col">
      {/* Header */}
      <header className="bg-white border-b border-[#E5E5EA] sticky top-0 z-50">
        <div className="max-w-3xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between mb-4">
            <Link to="/dashboard" className="flex items-center gap-2 text-[#6B7280] hover:text-[#111111] transition-colors">
              <ArrowLeft className="w-5 h-5" />
              <span className="font-medium">Salir</span>
            </Link>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-[#002FA7] to-[#5B21B6] flex items-center justify-center p-1">
                <FuturaIcon className="w-full h-full text-white" />
              </div>
              <span className="font-['Outfit'] font-semibold text-lg text-[#111111]">FUTURA</span>
            </div>
          </div>
          
          {/* Progress Bar */}
          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="text-[#6B7280]">Progreso del análisis</span>
              <span className="font-medium text-[#111111]">{progress.current}/{progress.total}</span>
            </div>
            <div className="h-2 bg-[#E5E5EA] rounded-full overflow-hidden">
              <div 
                className="h-full bg-gradient-to-r from-[#002FA7] to-[#5B21B6] transition-all duration-500"
                style={{ width: `${(progress.current / progress.total) * 100}%` }}
              />
            </div>
          </div>
        </div>
      </header>

      {/* Chat Area */}
      <main className="flex-1 overflow-y-auto">
        <div className="max-w-3xl mx-auto px-6 py-8 space-y-6">
          {messages.map((msg) => (
            <div
              key={msg._id}
              className={`flex ${msg.type === "user" ? "justify-end" : "justify-start"} animate-fade-in`}
            >
              <div className={`chat-message ${msg.type === "user" ? "chat-message-user" : "chat-message-ai"} p-4`}>
                <p className="leading-relaxed">{msg.text}</p>
                
                {/* Options for select questions */}
                {msg.type === "ai" && msg.options && !simulating && msg._id === messages[messages.length - 1]?._id && (
                  <div className="mt-4 space-y-2">
                    {msg.options.map((option, optIndex) => (
                      <button
                        key={option}
                        onClick={() => handleSubmitAnswer(option)}
                        disabled={submitting}
                        className="w-full text-left p-3 bg-white hover:bg-[#F5F5F7] rounded-xl border border-[#E5E5EA] hover:border-[#002FA7] text-[#111111] transition-all text-sm"
                        data-testid={`option-${optIndex}`}
                      >
                        {option}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}

          {simulating && (
            <div className="flex justify-center py-8">
              <div className="flex flex-col items-center gap-4">
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#002FA7] to-[#5B21B6] flex items-center justify-center animate-pulse-soft">
                  <FuturaIcon className="w-8 h-8 text-white" />
                </div>
                <div className="text-center max-w-md px-4" data-testid="sim-loading-message">
                  <p className="font-['Outfit'] font-semibold text-[#111111]">Generando simulación</p>
                  <p className="text-[#6B7280] text-sm mt-1">Estamos procesando su simulación, esto puede tardar hasta 1 minuto.</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>

      {/* Input Area */}
      {!simulating && currentQuestion?.question_type === "text" && (
        <div className="bg-white border-t border-[#E5E5EA] p-4">
          <div className="max-w-3xl mx-auto">
            <form 
              onSubmit={(e) => { e.preventDefault(); handleSubmitAnswer(); }}
              className="flex items-center gap-3"
            >
              <Input
                value={answer}
                onChange={(e) => setAnswer(e.target.value)}
                placeholder="Escribe tu respuesta..."
                className="flex-1 h-12 bg-[#F5F5F7] border-none rounded-full px-6 focus:ring-2 focus:ring-[#002FA7]"
                disabled={submitting}
                data-testid="chat-input"
              />
              <Button
                type="submit"
                disabled={submitting || !answer.trim()}
                className="w-12 h-12 rounded-full bg-[#002FA7] hover:bg-[#001d6a] p-0"
                data-testid="chat-submit-btn"
              >
                {submitting ? (
                  <Loader2 className="w-5 h-5 text-white animate-spin" />
                ) : (
                  <Send className="w-5 h-5 text-white" />
                )}
              </Button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default SimulationChat;
